import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { loadVerbsCsv } from './utils/loadVerbsCsv';
import { buildParticiplesFromRoot } from './utils/participlesFormI_salim';
import { buildParticiplesFromRowNonFormI } from './utils/participlesNonFormI';
import html2canvas from 'html2canvas';
import './App.css';
import './ParticiplesGame.css';

const PARTICIPLE_TYPES = [
  { id: 'active', nameHeb: 'בינוני פועל', nameAr: 'اسم الفاعل' },
  { id: 'passive', nameHeb: 'בינוני פעול', nameAr: 'اسم المفعول' }
];

const GENDER_OPTIONS = [
  { id: 'masculine', nameHeb: 'יחיד', rotation: -60 },
  { id: 'feminine', nameHeb: 'יחידה', rotation: -30 },
  { id: 'plural', nameHeb: 'רבים', rotation: 30 },
  { id: 'pluralFeminine', nameHeb: 'רבות', rotation: 60 }
];

const DEFAULT_GIZRA_PATTERNS = ['שלמים'];
const DEFAULT_GAME_CONFIG = {
  title: 'בינוני פועל ופעול - בניין 1',
  summaryTitle: 'בינוני פועל ופעול - בניין 1',
  binyan: '1',
  csvPath: '/verbs-grade10.csv',
  allowedGizraPatterns: DEFAULT_GIZRA_PATTERNS,
  buildParticiples: buildParticiplesFromRoot,
  shareFileName: 'תוצאות-בינוני-פועל-פעול-בניין-1.png'
};

export const PARTICIPLES_GAME_CONFIGS = {
  binyan2: {
    title: 'בינוני פועל ופעול - בניין 2',
    summaryTitle: 'בינוני פועל ופעול - בניין 2',
    binyan: '2',
    csvPath: '/verbs-grade10.csv',
    allowedGizraPatterns: ['שלמים', 'כפולים', 'ע"ו', 'פ"ו'],
    buildParticiples: buildParticiplesFromRowNonFormI,
    shareFileName: 'תוצאות-בינוני-פועל-פעול-בניין-2.png'
  },
  binyan3: {
    title: 'בינוני פועל ופעול - בניין 3',
    summaryTitle: 'בינוני פועל ופעול - בניין 3',
    binyan: '3',
    csvPath: '/verbs-grade10.csv',
    allowedGizraPatterns: ['שלמים', 'ע"ו', 'פ"ו'],
    buildParticiples: buildParticiplesFromRowNonFormI,
    shareFileName: 'תוצאות-בינוני-פועל-פעול-בניין-3.png'
  },
  binyan4: {
    title: 'בינוני פועל ופעול - בניין 4',
    summaryTitle: 'בינוני פועל ופעול - בניין 4',
    binyan: '4',
    csvPath: '/verbs-grade10.csv',
    allowedGizraPatterns: ['שלמים'],
    buildParticiples: buildParticiplesFromRowNonFormI,
    shareFileName: 'תוצאות-בינוני-פועל-פעול-בניין-4.png'
  }
};

// Helper function to check if a participle type and gender combination is allowed
const isAllowed = (allowedValue, genderId) => {
  if (!allowedValue || allowedValue.trim() === '') return false;
  
  const genderMap = {
    'masculine': 'sm',
    'feminine': 'sf',
    'plural': 'pm',
    'pluralFeminine': 'pf'
  };
  
  const allowedStr = allowedValue.toLowerCase().trim();
  if (allowedStr === 'all') return true;
  
  const genderCode = genderMap[genderId];
  if (!genderCode) return false;
  
  const allowedCodes = allowedStr.split(',').map(s => s.trim());
  return allowedCodes.includes(genderCode);
};

function ParticiplesGame({ gameConfig = DEFAULT_GAME_CONFIG }) {
  const navigate = useNavigate();
  const { grade } = useParams();
  const location = useLocation();
  const fromBinyan = location.state?.fromBinyan;
  const config = {
    ...DEFAULT_GAME_CONFIG,
    ...gameConfig
  };
  const {
    allowedGizraPatterns,
    binyan: configBinyan,
    buildParticiples,
    csvPath,
    shareFileName,
    summaryTitle,
    title
  } = config;
  
  const getBackUrl = () => {
    if (fromBinyan) {
      return `/grade/${grade}/verbs/binyan/${fromBinyan}`;
    }
    return `/grade/${grade}/verbs`;
  };
  
  const [questions, setQuestions] = useState([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [topArrowIndex, setTopArrowIndex] = useState(0);
  const [bottomArrowIndex, setBottomArrowIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [gameFinished, setGameFinished] = useState(false);
  const [userAnswers, setUserAnswers] = useState([]);
  const [showResultsPopup, setShowResultsPopup] = useState(false);
  const [isDraggingTop, setIsDraggingTop] = useState(false);
  const [topArrowAngle, setTopArrowAngle] = useState(0);
  const [isDraggingBottom, setIsDraggingBottom] = useState(false);
  const [bottomArrowAngle, setBottomArrowAngle] = useState(0);
  const [showFireworks, setShowFireworks] = useState(false);
  const [correctTopArrow, setCorrectTopArrow] = useState(false);
  const [correctBottomArrow, setCorrectBottomArrow] = useState(false);
  const [incorrectTopArrow, setIncorrectTopArrow] = useState(false);
  const [incorrectBottomArrow, setIncorrectBottomArrow] = useState(false);
  const [isAnimatingToCorrect, setIsAnimatingToCorrect] = useState(false);
  const [topArrowMoved, setTopArrowMoved] = useState(false);
  const [bottomArrowMoved, setBottomArrowMoved] = useState(false);
  const resultsSummaryRef = useRef(null);
  const audioContextRef = useRef(null);
  const clockRef = useRef(null);

  const initAudioContext = () => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (audioContextRef.current.state === 'suspended') {
      audioContextRef.current.resume();
    }
  };

  useEffect(() => {
    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  const playSound = (frequency, duration, type = 'success') => {
    initAudioContext();
    if (!audioContextRef.current) return;
    
    const audioContext = audioContextRef.current;
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    if (type === 'success') {
      oscillator.frequency.value = frequency;
      oscillator.type = 'sine';
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + duration);
    } else {
      oscillator.frequency.value = frequency;
      oscillator.type = 'sawtooth';
      gainNode.gain.setValueAtTime(0.2, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + duration);
    }
    
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + duration);
  };

  useEffect(() => {
    const loadQuestions = async () => {
      try {
        const rows = await loadVerbsCsv(csvPath);
        
        const filtered = rows.filter(r => {
          const binyan = (r['בניין'] || '').trim();
          const gizra = (r['gizra'] || '').trim();
          const tense = (r['זמן'] || '').trim();
          const matchesBinyan = binyan === configBinyan || binyan === `בניין ${configBinyan}`;
          const matchesGizra = allowedGizraPatterns.some(pattern => gizra.includes(pattern));
          const isPast = tense === 'עבר';

          return matchesBinyan && matchesGizra && isPast;
        });

        // Build separate lists for active and passive participles
        const activeQuestions = [];
        const passiveQuestions = [];
        
        for (const row of filtered) {
          const participles = buildParticiples(row);
          if (!participles) continue;
          
          const benoniPoelAllowed = row['benoni_poel_allowed'] || '';
          const benoniPaulAllowed = row['benoni_paul_allowed'] || '';
          
          // Check each combination of type and gender
          const genderOptions = ['masculine', 'feminine', 'plural', 'pluralFeminine'];
          
          for (const genderId of genderOptions) {
            // Check active (בינוני פועל)
            if (isAllowed(benoniPoelAllowed, genderId)) {
              const form = participles.active[genderId];
              activeQuestions.push({
                word: form,
                correctType: 'active',
                correctGender: genderId,
                verbBase: row['פועל'] || '',
                root: participles.root
              });
            }
            
            // Check passive (בינוני פעול)
            if (isAllowed(benoniPaulAllowed, genderId)) {
              const form = participles.passive[genderId];
              passiveQuestions.push({
                word: form,
                correctType: 'passive',
                correctGender: genderId,
                verbBase: row['פועל'] || '',
                root: participles.root
              });
            }
          }
        }

        // Helper function to select questions with balanced gender distribution.
        // It prefers roots that have not appeared yet, while keeping table-approved options only.
        const selectBalanced = (questions, count, globalUsedRoots = new Set()) => {
          // Group by gender
          const byGender = {
            masculine: [],
            feminine: [],
            plural: [],
            pluralFeminine: []
          };
          
          questions.forEach(q => {
            if (byGender[q.correctGender]) {
              byGender[q.correctGender].push(q);
            }
          });
          
          // Shuffle each gender group
          Object.keys(byGender).forEach(gender => {
            byGender[gender].sort(() => Math.random() - 0.5);
          });
          
          // Select questions evenly from each gender
          const selected = [];
          const localUsedRoots = new Set();
          const genders = ['masculine', 'feminine', 'plural', 'pluralFeminine'];
          let genderIndex = 0;
          let emptyCycles = 0;
          
          while (selected.length < count && selected.length < questions.length) {
            const gender = genders[genderIndex % genders.length];
            if (byGender[gender].length > 0) {
              const options = byGender[gender];
              let selectedIndex = options.findIndex(q => !globalUsedRoots.has(q.root) && !localUsedRoots.has(q.root));
              if (selectedIndex === -1) {
                selectedIndex = options.findIndex(q => !localUsedRoots.has(q.root));
              }
              if (selectedIndex === -1) {
                selectedIndex = 0;
              }

              const [question] = options.splice(selectedIndex, 1);
              selected.push(question);
              if (question.root) {
                localUsedRoots.add(question.root);
                globalUsedRoots.add(question.root);
              }
              emptyCycles = 0;
            } else {
              emptyCycles++;
            }
            genderIndex++;
            
            // Safety check: if we've cycled through all genders and none have items, break
            if (emptyCycles >= genders.length) break;
          }
          
          return selected;
        };

        // Select 5 from each type with balanced gender distribution
        const usedRoots = new Set();
        const selectedActive = selectBalanced(activeQuestions, 5, usedRoots);
        const selectedPassive = selectBalanced(passiveQuestions, 5, usedRoots);
        
        // Combine and shuffle the final list
        const combined = [...selectedActive, ...selectedPassive];
        const finalShuffled = combined.sort(() => Math.random() - 0.5);
        
        setQuestions(finalShuffled);
      } catch (error) {
        console.error('שגיאה בטעינת פעלים:', error);
      } finally {
        setIsLoaded(true);
      }
    };

    loadQuestions();
  }, [allowedGizraPatterns, buildParticiples, configBinyan, csvPath]);

  const handleBackToGames = () => {
    navigate(getBackUrl());
  };

  const calculateAngleFromCenter = (clientX, clientY) => {
    if (!clockRef.current) return 0;
    
    const rect = clockRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    
    const deltaX = clientX - centerX;
    const deltaY = clientY - centerY;
    
    let angle = Math.atan2(deltaY, deltaX) * (180 / Math.PI);
    angle = angle + 90;
    
    return angle;
  };

  const handleTopArrowPointerDown = (e) => {
    // Prevent dragging during animations
    if (showFireworks || isAnimatingToCorrect) return;
    
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingTop(true);
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handleTopArrowPointerMove = (e) => {
    if (!isDraggingTop) return;
    
    e.preventDefault();
    const angle = calculateAngleFromCenter(e.clientX, e.clientY);
    setTopArrowAngle(angle);
  };

  const handleTopArrowPointerUp = (e) => {
    e.preventDefault();
    
    // Always release pointer capture, even if not dragging
    try {
      if (e.currentTarget && e.currentTarget.hasPointerCapture && e.currentTarget.hasPointerCapture(e.pointerId)) {
        e.currentTarget.releasePointerCapture(e.pointerId);
      }
    } catch (err) {
      console.log('Could not release pointer capture:', err);
    }
    
    if (!isDraggingTop) return;
    
    setIsDraggingTop(false);
    setTopArrowMoved(true); // Mark that top arrow has been moved
    
    // Use the current arrow angle (which reflects where the arrow is pointing)
    let finalAngle = topArrowAngle;
    
    // Normalize to [-180, 180] range
    while (finalAngle > 180) finalAngle -= 360;
    while (finalAngle < -180) finalAngle += 360;
    
    // Left side (angle < 0) = passive (בינוני פעול, index 1)
    // Right side (angle >= 0) = active (בינוני פועל, index 0)
    if (finalAngle < 0) {
      setTopArrowIndex(1); // passive (left)
      setTopArrowAngle(-45);
    } else {
      setTopArrowIndex(0); // active (right)
      setTopArrowAngle(45);
    }
  };

  const handleBottomArrowPointerDown = (e) => {
    // Prevent dragging during animations
    if (showFireworks || isAnimatingToCorrect) return;
    
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingBottom(true);
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handleBottomArrowPointerMove = (e) => {
    if (!isDraggingBottom) return;
    
    e.preventDefault();
    let angle = calculateAngleFromCenter(e.clientX, e.clientY);
    // Offset by 180° because bottom arrow points down, not up
    angle = angle - 180;
    // Normalize to [-180, 180]
    while (angle > 180) angle -= 360;
    while (angle < -180) angle += 360;
    setBottomArrowAngle(angle);
  };

  const handleBottomArrowPointerUp = (e) => {
    e.preventDefault();
    
    // Always release pointer capture, even if not dragging
    try {
      if (e.currentTarget && e.currentTarget.hasPointerCapture && e.currentTarget.hasPointerCapture(e.pointerId)) {
        e.currentTarget.releasePointerCapture(e.pointerId);
      }
    } catch (err) {
      console.log('Could not release pointer capture:', err);
    }
    
    if (!isDraggingBottom) return;
    
    setIsDraggingBottom(false);
    setBottomArrowMoved(true); // Mark that bottom arrow has been moved
    
    const allowedAngles = [60, -60, 30, -30];
    let finalAngle = bottomArrowAngle;
    
    // Already normalized in handleBottomArrowPointerMove
    
    // Find nearest allowed angle
    let nearestAngle = allowedAngles[0];
    let minDiff = Math.abs(finalAngle - nearestAngle);
    
    for (const angle of allowedAngles) {
      const diff = Math.abs(finalAngle - angle);
      if (diff < minDiff) {
        minDiff = diff;
        nearestAngle = angle;
      }
    }
    
    // Map angle to index
    const angleToIndex = {
      60: 3,      // pluralFeminine (רבות)
      '-60': 0,   // masculine (יחיד)
      30: 2,      // plural (רבים)
      '-30': 1    // feminine (יחידה)
    };
    
    setBottomArrowAngle(nearestAngle);
    setBottomArrowIndex(angleToIndex[nearestAngle.toString()]);
  };

  const handleTopLabelClick = (typeId) => {
    // Prevent clicking during game animations
    if (showFireworks || isAnimatingToCorrect) return;
    
    if (typeId === 'active') {
      setTopArrowIndex(0);
      setTopArrowAngle(45);
      setTopArrowMoved(true);
    } else if (typeId === 'passive') {
      setTopArrowIndex(1);
      setTopArrowAngle(-45);
      setTopArrowMoved(true);
    }
  };

  const handleBottomLabelClick = (genderId) => {
    // Prevent clicking during game animations
    if (showFireworks || isAnimatingToCorrect) return;
    
    const genderIndex = GENDER_OPTIONS.findIndex(g => g.id === genderId);
    if (genderIndex === -1) return;
    
    const rotation = GENDER_OPTIONS[genderIndex].rotation;
    setBottomArrowIndex(genderIndex);
    setBottomArrowAngle(rotation);
    setBottomArrowMoved(true);
  };

  const checkAnswer = () => {
    // Prevent multiple submissions
    if (!questions[currentIndex] || showFireworks || isAnimatingToCorrect) return;
    
    const question = questions[currentIndex];
    const selectedType = PARTICIPLE_TYPES[topArrowIndex].id;
    const selectedGender = GENDER_OPTIONS[bottomArrowIndex].id;
    
    const isCorrect = selectedType === question.correctType && 
                     selectedGender === question.correctGender;
    
    const answerRecord = {
      word: question.word,
      verbBase: question.verbBase,
      correctType: question.correctType,
      correctGender: question.correctGender,
      userType: selectedType,
      userGender: selectedGender,
      isCorrect: isCorrect
    };
    
    setUserAnswers(prev => [...prev, answerRecord]);
    
    if (isCorrect) {
      setScore(s => s + 1);
      
      // Show fireworks instead of popup
      setShowFireworks(true);
      setCorrectTopArrow(true);
      setCorrectBottomArrow(true);
      
      // Play success sounds
      playSound(523.25, 0.15);
      setTimeout(() => playSound(659.25, 0.15), 100);
      setTimeout(() => playSound(783.99, 0.2), 200);
      
      // Move to next question after 1500ms (allow time for all fireworks to display)
      setTimeout(() => {
        setShowFireworks(false);
        setCorrectTopArrow(false);
        setCorrectBottomArrow(false);
        
        if (currentIndex < questions.length - 1) {
          setCurrentIndex(prev => prev + 1);
          setTopArrowIndex(0);
          setTopArrowAngle(0);
          setBottomArrowIndex(0);
          setBottomArrowAngle(0);
          setTopArrowMoved(false);
          setBottomArrowMoved(false);
        } else {
          setGameFinished(true);
        }
      }, 1500);
    } else {
      playSound(200, 0.3, 'error');
      
      // Mark incorrect arrows in red
      const isTopIncorrect = selectedType !== question.correctType;
      const isBottomIncorrect = selectedGender !== question.correctGender;
      
      if (isTopIncorrect) setIncorrectTopArrow(true);
      if (isBottomIncorrect) setIncorrectBottomArrow(true);
      
      // Set animating flag to prevent multiple submissions
      setIsAnimatingToCorrect(true);
      
      // After 800ms, animate arrows to correct answer
      setTimeout(() => {
        
        // Find correct indices
        const correctTypeIndex = PARTICIPLE_TYPES.findIndex(t => t.id === question.correctType);
        const correctGenderIndex = GENDER_OPTIONS.findIndex(g => g.id === question.correctGender);
        
        // Animate arrows to correct position
        if (isTopIncorrect) {
          const correctTopAngle = correctTypeIndex === 0 ? 45 : -45;
          setTopArrowAngle(correctTopAngle);
          setTopArrowIndex(correctTypeIndex);
        }
        
        if (isBottomIncorrect) {
          const correctBottomAngle = GENDER_OPTIONS[correctGenderIndex].rotation;
          setBottomArrowAngle(correctBottomAngle);
          setBottomArrowIndex(correctGenderIndex);
        }
        
        // Clear red marks and show correct highlighting only for incorrect parts
        setIncorrectTopArrow(false);
        setIncorrectBottomArrow(false);
        
        // Only highlight in green the parts that were wrong
        if (isTopIncorrect) setCorrectTopArrow(true);
        if (isBottomIncorrect) setCorrectBottomArrow(true);
        
        // After showing correct answer for 2 seconds, automatically move to next question
        setTimeout(() => {
          // Reset states (only reset what was set)
          if (isTopIncorrect) setCorrectTopArrow(false);
          if (isBottomIncorrect) setCorrectBottomArrow(false);
          setIsAnimatingToCorrect(false);
          
          // Move to next question
          if (currentIndex < questions.length - 1) {
            setCurrentIndex(prev => prev + 1);
            setTopArrowIndex(0);
            setTopArrowAngle(0);
            setBottomArrowIndex(0);
            setBottomArrowAngle(0);
            setTopArrowMoved(false);
            setBottomArrowMoved(false);
          } else {
            setGameFinished(true);
          }
        }, 2500);
      }, 800);
    }
  };


  const generateResultsImage = async () => {
    if (!resultsSummaryRef.current) return null;
    const canvas = await html2canvas(resultsSummaryRef.current, {
      backgroundColor: '#5eb9c5',
      scale: 2,
    });
    return canvas.toDataURL('image/png');
  };

  const shareViaWhatsApp = async () => {
    const imageData = await generateResultsImage();
    if (!imageData) return;

    const blob = dataURLtoBlob(imageData);
    const fileName = shareFileName;
    const file = new File([blob], fileName, { type: 'image/png' });

    if (navigator.share && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({
          title: `תוצאות ${summaryTitle}`,
          text: `השגתי ${score}/${questions.length}!`,
          files: [file],
        });
      } catch (err) {
        console.error('Error sharing:', err);
      }
    } else {
      const link = document.createElement('a');
      link.download = fileName;
      link.href = imageData;
      link.click();
    }
    setShowResultsPopup(false);
  };

  const shareViaEmail = async () => {
    const imageData = await generateResultsImage();
    if (!imageData) return;
    const fileName = shareFileName;
    const link = document.createElement('a');
    link.download = fileName;
    link.href = imageData;
    link.click();
    setShowResultsPopup(false);
  };

  const dataURLtoBlob = (dataURL) => {
    const parts = dataURL.split(',');
    const mime = parts[0].match(/:(.*?);/)[1];
    const bstr = atob(parts[1]);
    const u8arr = new Uint8Array(bstr.length);
    for (let n = 0; n < bstr.length; n++) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  };

  if (!isLoaded) {
    return (
      <div className="App participles-game">
        <div className="loading">טוען...</div>
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <div className="App participles-game">
        <header className="App-header">
          <div className="header-with-back" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative', width: '100%' }}>
            <button onClick={handleBackToGames} className="back-to-grade-button" style={{ position: 'absolute', right: '20px', top: '-44px' }}>
              חזרה למשחקי הפועל ↩
            </button>
            <h1 className="game-title-with-back" style={{ margin: 0 }}>{title}</h1>
          </div>
        </header>
        <main className="game-container benoni results-screen">
          <div className="words-summary">
            <h3>לא נמצאו שאלות מתאימות למשחק הזה.</h3>
          </div>
        </main>
      </div>
    );
  }

  if (gameFinished) {
    return (
      <div className="App participles-game">
        <header className="App-header">
          <div className="header-with-back" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative', width: '100%' }}>
            <button onClick={handleBackToGames} className="back-to-grade-button" style={{ position: 'absolute', right: '20px', top: '-44px' }}>
              חזרה למשחקי הפועל ↩
            </button>
            <h1 className="game-title-with-back" style={{ margin: 0 }}>תוצאות המשחק</h1>
          </div>
        </header>
        <main className="game-container benoni results-screen">
          <div className="words-summary" ref={resultsSummaryRef}>
            <h3>{summaryTitle}</h3>
            <div className="final-score-display">
              ניקוד: {score}/{questions.length}
            </div>
            <br/>
            <div className="words-list">
              {userAnswers.map((answer, index) => (
                <div key={index} className="word-item">
                  <div className="word-pair">
                    <span className="arabic-word-summary">{answer.word}</span>
                    <span className="separator"> - </span>
                    <span className="hebrew-word">
                      {PARTICIPLE_TYPES.find(t => t.id === answer.correctType)?.nameHeb} - {GENDER_OPTIONS.find(g => g.id === answer.correctGender)?.nameHeb}
                    </span>
                  </div>
                  <div className={`attempts-info ${!answer.isCorrect ? 'multiple-attempts' : ''}`}>
                    {answer.isCorrect ? (
                      <span>✓ תשובה נכונה</span>
                    ) : (
                      <span>✗ תשובתך: {PARTICIPLE_TYPES.find(t => t.id === answer.userType)?.nameHeb} - {GENDER_OPTIONS.find(g => g.id === answer.userGender)?.nameHeb}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="game-end-buttons">
              <button onClick={() => window.location.reload()} className="game-button restart-button">
                שחק מחדש
              </button>
              <button onClick={() => setShowResultsPopup(true)} className="game-button send-button">
                שתף תוצאות
              </button>
            </div>
          </div>

          {showResultsPopup && (
            <div className="popup-overlay" onClick={() => setShowResultsPopup(false)}>
              <div className="popup-content" onClick={(e) => e.stopPropagation()}>
                <button className="popup-close-button" onClick={() => setShowResultsPopup(false)}>
                  ×
                </button>
                <h3>שתף את התוצאות</h3>
                <p>בחר איך תרצה לשתף את התוצאות שלך:</p>
                <div className="popup-buttons">
                  <button onClick={shareViaEmail} className="popup-button email-button">
                    💾 הורד תמונה
                  </button>
                  <button onClick={shareViaWhatsApp} className="popup-button whatsapp-button">
                    📱 שתף בוואטסאפ
                  </button>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    );
  }

  const currentQuestion = questions[currentIndex];
  const topRotation = topArrowAngle;
  const bottomRotation = bottomArrowAngle;

  return (
    <div className="App participles-game">
      <header className="App-header">
        <div className="header-with-back" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative', width: '100%' }}>
          <button onClick={handleBackToGames} className="back-to-grade-button" style={{ position: 'absolute', right: '20px', top: '-44px' }}>
            חזרה למשחקי הפועל ↩
          </button>
          <h1 className="game-title-with-back" style={{ margin: 0 }}>{title}</h1>
        </div>
        <p className="game-instructions-animate">סובב את המחוג העליון לבינוני ואת המחוג התחתון למין ולמספר.</p>
      </header>

      <div className="game-container" style={{ position: 'relative' }}>
        {showFireworks && (
          <div className="fireworks-container">
            <div className="firework firework-1"></div>
            <div className="firework firework-2"></div>
            <div className="firework firework-3"></div>
            <div className="firework firework-4"></div>
            <div className="firework firework-5"></div>
            <div className="firework firework-6"></div>
            <div className="firework firework-7"></div>
            <div className="firework firework-8"></div>
            <div className="firework firework-9"></div>
            <div className="firework firework-10"></div>
            <div className="firework firework-11"></div>
            <div className="firework firework-12"></div>
          </div>
        )}
        
        <div 
          className="clock-interface"
          ref={clockRef}
        >
          <div className="clock-labels">
            <div 
              className={`clock-label active-participle ${
                correctTopArrow && PARTICIPLE_TYPES[topArrowIndex].id === 'active' ? 'correct pulse' : ''
              } ${
                incorrectTopArrow && PARTICIPLE_TYPES[topArrowIndex].id === 'active' ? 'incorrect' : ''
              }`}
              onClick={() => handleTopLabelClick('active')}
              style={{ cursor: 'pointer' }}
            >
              <span className="label-word">בינוני</span> <span className="label-word">פועל</span>
            </div>
            <div 
              className={`clock-label passive-participle ${
                correctTopArrow && PARTICIPLE_TYPES[topArrowIndex].id === 'passive' ? 'correct pulse' : ''
              } ${
                incorrectTopArrow && PARTICIPLE_TYPES[topArrowIndex].id === 'passive' ? 'incorrect' : ''
              }`}
              onClick={() => handleTopLabelClick('passive')}
              style={{ cursor: 'pointer' }}
            >
              <span className="label-word">בינוני</span> <span className="label-word">פעול</span>
            </div>
            <div 
              className={`clock-label masculine ${
                correctBottomArrow && GENDER_OPTIONS[bottomArrowIndex].id === 'masculine' ? 'correct pulse' : ''
              } ${
                incorrectBottomArrow && GENDER_OPTIONS[bottomArrowIndex].id === 'masculine' ? 'incorrect' : ''
              }`}
              onClick={() => handleBottomLabelClick('masculine')}
              style={{ cursor: 'pointer' }}
            >
              יחיד
            </div>
            <div 
              className={`clock-label feminine ${
                correctBottomArrow && GENDER_OPTIONS[bottomArrowIndex].id === 'feminine' ? 'correct pulse' : ''
              } ${
                incorrectBottomArrow && GENDER_OPTIONS[bottomArrowIndex].id === 'feminine' ? 'incorrect' : ''
              }`}
              onClick={() => handleBottomLabelClick('feminine')}
              style={{ cursor: 'pointer' }}
            >
              יחידה
            </div>
            <div 
              className={`clock-label plural ${
                correctBottomArrow && GENDER_OPTIONS[bottomArrowIndex].id === 'plural' ? 'correct pulse' : ''
              } ${
                incorrectBottomArrow && GENDER_OPTIONS[bottomArrowIndex].id === 'plural' ? 'incorrect' : ''
              }`}
              onClick={() => handleBottomLabelClick('plural')}
              style={{ cursor: 'pointer' }}
            >
              רבים
            </div>
            <div 
              className={`clock-label plural-feminine ${
                correctBottomArrow && GENDER_OPTIONS[bottomArrowIndex].id === 'pluralFeminine' ? 'correct pulse' : ''
              } ${
                incorrectBottomArrow && GENDER_OPTIONS[bottomArrowIndex].id === 'pluralFeminine' ? 'incorrect' : ''
              }`}
              onClick={() => handleBottomLabelClick('pluralFeminine')}
              style={{ cursor: 'pointer' }}
            >
              רבות
            </div>
          </div>

          <div className="clock-word">
            {currentQuestion.word}
            <span className="question-counter">{currentIndex + 1} / {questions.length}</span>
          </div>

          <div 
            className={`clock-arrow top ${isDraggingTop ? 'dragging' : ''}`}
            style={{ transform: `translate(-50%, -100%) rotate(${topRotation}deg)`, touchAction: 'none' }}
            onPointerDown={handleTopArrowPointerDown}
            onPointerMove={handleTopArrowPointerMove}
            onPointerUp={handleTopArrowPointerUp}
            onPointerCancel={handleTopArrowPointerUp}
          />
          
          <div 
            className={`clock-arrow bottom ${isDraggingBottom ? 'dragging' : ''}`}
            style={{ transform: `translate(-50%, 0%) rotate(${bottomRotation}deg)`, touchAction: 'none' }}
            onPointerDown={handleBottomArrowPointerDown}
            onPointerMove={handleBottomArrowPointerMove}
            onPointerUp={handleBottomArrowPointerUp}
            onPointerCancel={handleBottomArrowPointerUp}
          />

          <div className="clock-center" />
        </div>

        <button 
          className="check-button" 
          onClick={checkAnswer} 
          disabled={showFireworks || isAnimatingToCorrect || !topArrowMoved || !bottomArrowMoved}
        >
          בדוק תשובה
        </button>
      </div>

    </div>
  );
}

export default ParticiplesGame;
